import { CreateMetaSiteHandlerRequest, CreateMetaSiteHandlerResponse } from 'server/handlers/createMetaSite';
import { httpUtils } from 'utils/http';
import { ENDPOINTS } from 'config/endpoints';
import { UploadSiteMediaHandlerRequest, UploadSiteMediaHandlerResponse } from 'server/handlers/uploadSiteMedia';
import { GenerateMockupsHandlerRequest, GenerateMockupsHandlerResponse } from 'server/handlers/generateMockups';
import {
  AttachSiteTemplateHandlerRequest,
  AttachSiteTemplateHandlerResponse,
} from 'server/handlers/attachSiteTemplate';
import { InstallSiteAppsHandlerRequest, InstallSiteAppsHandlerResponse } from 'server/handlers/installSiteApps';
import { GetSiteUrlsHandlerRequest, GetSiteUrlsHandlerResponse } from 'server/handlers/getSiteUrls';
import { AddStoreProductsHandlerRequest, AddStoreProductsHandlerResponse } from 'server/handlers/addStoreProducts';
import { GetProductsCountHandlerRequest, GetProductsCountHandlerResponse } from 'server/handlers/getProductsCount';
import {
  GetRecommendedProductsHandlerRequest,
  GetRecommendedProductsHandlerResponse,
} from 'types/recommended-products';
import type { GetInstalledAppsHandlerRequest, GetInstalledAppsHandlerResponse } from 'types/installed-apps';
import type { GetSiteImagesHandlerRequest, GetSiteImagesHandlerResponse } from 'types/site-images';
import {
  updateSiteSettings as updateSiteSettingsHttp,
  suggestStoreName as suggestStoreNameHttp,
  publishSite as publishSiteHttp,
  deleteAllProducts as deleteAllProductsHttp,
} from '@wix/ambassador-gg-store-setup-v1-store-setup-service/http';
import {
  UpdateSiteSettingsRequest,
  SuggestStoreNameRequest,
  StoreProductsDeletionStrategy,
} from '@wix/ambassador-gg-store-setup-v1-store-setup-service/types';
import {
  InjectComponentCoreHandlerRequest,
  InjectComponentCoreHandlerResponse,
} from 'server/handlers/injectComponentCore';

const createMetaSite = async (params: CreateMetaSiteHandlerRequest) => {
  const response = await httpUtils.client.post<CreateMetaSiteHandlerResponse>(ENDPOINTS.CREATE_META_SITE, params);
  return response.data;
};

const uploadSiteMedia = async (params: UploadSiteMediaHandlerRequest) => {
  const data = new FormData();
  data.append('media', params.media);
  const response = await httpUtils.client.post<UploadSiteMediaHandlerResponse>(ENDPOINTS.UPLOAD_SITE_MEDIA, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

const generateMockups = async (params: GenerateMockupsHandlerRequest) => {
  const response = await httpUtils.client.post<GenerateMockupsHandlerResponse>(ENDPOINTS.GENERATE_MOCKUPS, params);
  return response.data;
};

const attachSiteTemplate = async (params: AttachSiteTemplateHandlerRequest) => {
  const response = await httpUtils.client.post<AttachSiteTemplateHandlerResponse>(
    ENDPOINTS.ATTACH_SITE_TEMPLATE,
    params,
  );
  return response.data;
};

const installSiteApps = async (params: InstallSiteAppsHandlerRequest) => {
  const response = await httpUtils.client.post<InstallSiteAppsHandlerResponse>(ENDPOINTS.INSTALL_SITE_APPS, params);
  return response.data;
};

const publishSite = async () => {
  const response = await httpUtils.ambassador.request(publishSiteHttp({}));
  return response.data;
};

const deleteAllProducts = async () => {
  const response = await httpUtils.ambassador.request(
    deleteAllProductsHttp({ deletionStrategy: StoreProductsDeletionStrategy.WAIT_FOR_PRODUCTS }),
  );
  return response.data;
};

const getSiteUrls = async (params: GetSiteUrlsHandlerRequest) => {
  const response = await httpUtils.client.post<GetSiteUrlsHandlerResponse>(ENDPOINTS.GET_SITE_URLS, params);
  return response.data;
};

const addStoreProducts = async (params: AddStoreProductsHandlerRequest) => {
  const response = await httpUtils.client.post<AddStoreProductsHandlerResponse>(ENDPOINTS.ADD_STORE_PRODUCTS, params);
  return response.data;
};

const updateSiteSettings = async (params: UpdateSiteSettingsRequest) => {
  const response = await httpUtils.ambassador.request(updateSiteSettingsHttp(params));
  return response.data;
};

const injectComponentCore = async (params: InjectComponentCoreHandlerRequest) => {
  const response = await httpUtils.client.post<InjectComponentCoreHandlerResponse>(
    ENDPOINTS.INJECT_COMPONENT_CORE,
    params,
  );
  return response.data;
};

const suggestSiteNames = async (params: SuggestStoreNameRequest) => {
  const response = await httpUtils.ambassador.request(suggestStoreNameHttp(params));
  return response.data;
};

const getProductsCount = async (params: GetProductsCountHandlerRequest) => {
  const response = await httpUtils.client.post<GetProductsCountHandlerResponse>(ENDPOINTS.GET_PRODUCTS_COUNT, params);
  return response.data;
};

const getRecommendedProducts = async (params: GetRecommendedProductsHandlerRequest) => {
  const response = await httpUtils.client.post<GetRecommendedProductsHandlerResponse>(
    ENDPOINTS.GET_RECOMMENDED_PRODUCTS,
    params,
  );
  return response.data;
};

const getInstalledApps = async (params: GetInstalledAppsHandlerRequest = {}) => {
  const response = await httpUtils.client.post<GetInstalledAppsHandlerResponse>(ENDPOINTS.GET_INSTALLED_APPS, params);
  return response.data;
};

const getSiteImages = async (params: GetSiteImagesHandlerRequest = {}) => {
  const response = await httpUtils.client.post<GetSiteImagesHandlerResponse>(ENDPOINTS.GET_SITE_IMAGES, params);
  return response.data;
};

export const endpoints = {
  createMetaSite,
  uploadSiteMedia,
  generateMockups,
  attachSiteTemplate,
  installSiteApps,
  publishSite,
  deleteAllProducts,
  getSiteUrls,
  addStoreProducts,
  updateSiteSettings,
  injectComponentCore,
  suggestSiteNames,
  getProductsCount,
  getRecommendedProducts,
  getInstalledApps,
  getSiteImages,
};
